import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { AutomationProjections } from '@rossum/api-client/queues';
import { IconZoomIn, IconZoomOut } from '@rossum/ui/icons/tabler';
import {
  alpha,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Popper,
  Stack,
  SvgIcon,
  Typography,
  useTheme,
} from '@rossum/ui/material';
import {
  AllSeriesType,
  AreaPlot,
  ChartContainerPro,
  ChartsClipPath,
  ChartsGrid,
  ChartsReferenceLine,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  HighlightItemData,
  LinePlot,
  markElementClasses,
  MarkPlot,
  ScatterPlot,
  ZoomData,
} from '@rossum/ui/x-charts-pro';
import { OptimizationData } from '../../core/types';
import {
  convertErrorRateToPrecisionTarget,
  convertRateToPercentage,
  EMPTY_PLACEHOLDER,
} from '../../core/utils';
import {
  CHART_HEIGHT,
  convertErrorRatesIntoPrecisionTargets,
  getLineSelectTransition,
} from '../utils';
import {
  automationGradientId,
  CustomGradientLayer,
} from './CustomGradientLayer';
import { OPTIMIZATION_FORM_ID } from './OptimizeAutomationData';

const END_ZOOM_LIMIT = 50;
const START_ZOOM_LIMIT = 40;

export type OptimizeAutomationChartProps = {
  currentState: OptimizationData | null;
  automationProjections: AutomationProjections | undefined;
  isLoading: boolean;
  setTargetState: Dispatch<SetStateAction<OptimizationData | null>>;
  targetState: OptimizationData | null;
  isDirty: boolean;
};

const SELECTED_STATE_SERIES_ID = 'selected-state';
const CURRENT_STATE_SERIES_ID = 'current-state';
const STROKE_DASHARRAY = 5;
const X_AXIS_ID = 'x-axis';
const Y_AXIS_ID = 'y-axis';
const FADE_IN_ANIMATION = 'fadeIn 0.2s ease-in-out 0.2s forwards';

export const OptimizeAutomationChart = ({
  currentState,
  automationProjections,
  isLoading,
  setTargetState,
  targetState,
  isDirty,
}: OptimizeAutomationChartProps) => {
  const theme = useTheme();
  const intl = useIntl();

  const [shouldBlockZoom, setShouldBlockZoom] = useState(false);
  const [tooltipAnchor, setTooltipAnchor] = useState<HighlightItemData | null>(
    null
  );

  const [zoomData, setZoomData] = useState<ZoomData[]>([
    {
      axisId: X_AXIS_ID,
      end: 100,
      start: 0,
    },
    {
      axisId: Y_AXIS_ID,
      end: 100,
      start: 0,
    },
  ]);

  const { precisionTargets, automationRates } = useMemo(() => {
    if (isLoading || isDirty)
      return {
        precisionTargets: convertErrorRatesIntoPrecisionTargets([
          0, 0.12, 0.34, 0.66, 0.8,
        ]),
        automationRates: [0, 0.3, 0.4, 0.7, 0.8],
      };
    return {
      precisionTargets: convertErrorRatesIntoPrecisionTargets(
        automationProjections?.projections.map(
          data => data.estimatedErrorRate
        ) ?? []
      ),
      automationRates:
        automationProjections?.projections.map(
          data => data.documentAutomationRate
        ) ?? [],
    };
  }, [automationProjections, isLoading, isDirty]);

  // Relying on the native tooltip component of mui-charts when doing compositions is too flaky.
  // Problems occur when mixing line and scatter charts together, and we have very little control over things with little documentation
  const tooltipData = useMemo(() => {
    if (typeof tooltipAnchor?.dataIndex !== 'number' || !currentState)
      return null;

    const precisionTarget = precisionTargets[tooltipAnchor.dataIndex];
    const automationRate = automationRates[tooltipAnchor.dataIndex];

    const data =
      tooltipAnchor.seriesId === CURRENT_STATE_SERIES_ID
        ? currentState
        : tooltipAnchor.seriesId === SELECTED_STATE_SERIES_ID && targetState
          ? targetState
          : {
              precisionTarget,
              automationRate,
            };

    return {
      precisionTarget: convertRateToPercentage(data.precisionTarget),
      automationRate: convertRateToPercentage(data.automationRate),
      seriesId: tooltipAnchor.seriesId,
    };
  }, [
    tooltipAnchor,
    precisionTargets,
    automationRates,
    currentState,
    targetState,
  ]);

  const xAxisZoomData = zoomData.find(data => data.axisId === X_AXIS_ID);

  // <CHART-SERIES>

  // think of series as visual layers of the charts distributed across the y axis
  // we create a separate series for every separate ui behavior we want to display within ChartContainer
  // e.g. a serie for all the projects, two other series for current automation and selected states (these are scatter points somewhere in the chart),

  const selectedStateSerie: AllSeriesType[] = targetState
    ? [
        {
          data: [
            {
              x: targetState.precisionTarget,
              y: targetState.automationRate,
            },
          ],
          color: theme.palette.primary.main,
          type: 'scatter',
          id: SELECTED_STATE_SERIES_ID,
        },
      ]
    : [];

  const currentStateSerie: AllSeriesType = {
    data: currentState
      ? [
          {
            x: currentState.precisionTarget,
            y: currentState.automationRate,
          },
        ]
      : [],
    type: 'scatter',
    id: CURRENT_STATE_SERIES_ID,
  };

  const automationRatesSerie: AllSeriesType = {
    data: automationRates,
    type: 'line',
    area: true,
    id: 'automation-rate',
  };

  // </CHART-SERIES>

  // <CALLBACKS>

  const handleZoomChange = (value: number) => {
    setZoomData(prev => {
      return prev.map(data =>
        data.axisId === X_AXIS_ID
          ? {
              ...data,
              end: R.clamp(data.end - value, { max: 100, min: END_ZOOM_LIMIT }),
              start: R.clamp(data.start + value, {
                max: START_ZOOM_LIMIT,
                min: 0,
              }),
            }
          : data
      );
    });
  };

  // </CALLBACKS>

  return (
    <Stack>
      <Stack
        direction="row"
        sx={{ alignItems: 'flex-end', justifyContent: 'space-between' }}
      >
        <Stack sx={{ maxWidth: 500 }}>
          <Typography variant="h6">
            {intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.charts.optimizationChart.title',
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.charts.optimizationChart.description',
            })}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', justifyContent: 'center' }}
          useFlexGap
        >
          <IconButton
            onClick={() => handleZoomChange(-5)}
            disabled={isLoading || isDirty}
          >
            <SvgIcon fontSize="medium">
              <IconZoomOut />
            </SvgIcon>
          </IconButton>
          <Typography
            variant="body2"
            color={isLoading ? 'text.disabled' : 'text.secondary'}
            sx={{ width: 40, textAlign: 'center' }}
          >
            {xAxisZoomData
              ? R.subtract(
                  100,
                  R.floor(xAxisZoomData.end - xAxisZoomData.start, 0)
                )
              : 0}
            %
          </Typography>
          <IconButton
            onClick={() => handleZoomChange(5)}
            disabled={isLoading || isDirty}
          >
            <SvgIcon fontSize="medium">
              <IconZoomIn />
            </SvgIcon>
          </IconButton>
        </Stack>
      </Stack>
      <Stack sx={{ position: 'relative' }}>
        {isLoading ? (
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              textAlign: 'center',
            }}
          >
            <CircularProgress size={32} />
            <Stack sx={{ width: 0.8 }}>
              <Typography variant="h6">
                {intl.formatMessage({
                  id: 'containers.settings.queues.queue.automation.charts.optimizationChart.loading.title',
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({
                  id: 'containers.settings.queues.queue.automation.charts.optimizationChart.loading.description',
                })}
              </Typography>
            </Stack>
          </Stack>
        ) : null}
        {!isLoading && isDirty ? (
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              textAlign: 'center',
            }}
            spacing={1}
            useFlexGap
          >
            <Stack sx={{ width: 0.8 }}>
              <Typography variant="h6">
                {intl.formatMessage({
                  id: 'containers.settings.queues.queue.automation.charts.optimizationChart.dirtyOverlay.title',
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({
                  id: 'containers.settings.queues.queue.automation.charts.optimizationChart.dirtyOverlay.description',
                })}
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              color="secondary"
              type="submit"
              form={OPTIMIZATION_FORM_ID}
            >
              {intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.charts.optimizationChart.dirtyOverlay.button',
              })}
            </Button>
          </Stack>
        ) : null}
        <ChartContainerPro
          skipAnimation
          zoomData={zoomData}
          onZoomChange={axes => {
            if (!shouldBlockZoom)
              setZoomData(
                axes.map(axis => ({
                  ...axis,
                  end: R.clamp(axis.end, { max: 100, min: END_ZOOM_LIMIT }),
                  start: R.clamp(axis.start, { max: START_ZOOM_LIMIT, min: 0 }),
                }))
              );
          }}
          height={CHART_HEIGHT + 100}
          onHighlightChange={setTooltipAnchor}
          sx={{
            ...(isLoading || isDirty
              ? { filter: 'blur(3px)', pointerEvents: 'none' }
              : {}),
            [`& [data-series="${CURRENT_STATE_SERIES_ID}"]`]: {
              strokeWidth: 2,
              stroke: targetState
                ? theme.palette.text.disabled
                : theme.palette.primary.main,
              circle: {
                r: targetState ? 3 : 4,
                fill: theme.palette.common.white,
                cursor: 'pointer',
                '&:hover': {
                  r: 5,
                },
              },
            },
            [`& [data-series="${SELECTED_STATE_SERIES_ID}"]`]: {
              strokeWidth: 2,
              stroke: theme.palette.primary.main,
              circle: {
                r: 5,
                fill: theme.palette.common.white,
              },
            },
            '& [data-series="automation-rate"]': {
              circle: {
                fill: theme.palette.common.white,
                stroke: theme.palette.aurora.main,
                strokeWidth: 1.5,
                r: 4,
                '&:hover': {
                  r: 5,
                },
              },
            },
            // Animate ChartsReferenceLine position changes
            // only apply animation transitions when cursor is hovered over circle elements in svg
            // otherwise animations happen when zooming and dragging as well, which is not optimal.
            [`&:has([data-series="${CURRENT_STATE_SERIES_ID}"]:hover) .MuiChartsReferenceLine-root line,
              &:has([data-series="${CURRENT_STATE_SERIES_ID}"]:hover) g[class*="ChartsReferenceLine"] path,
              &:has([data-series="${CURRENT_STATE_SERIES_ID}"]:hover) #${automationGradientId}`]:
              {
                transition: getLineSelectTransition(theme, ['d']),
              },
            [`&:has(.${markElementClasses.root}:hover) .MuiChartsReferenceLine-root line,
              &:has(.${markElementClasses.root}:hover) g[class*="ChartsReferenceLine"] path,
              &:has(.${markElementClasses.root}:hover) #${automationGradientId}`]:
              {
                transition: getLineSelectTransition(theme, ['d']),
              },

            '@keyframes fadeIn': {
              from: {
                opacity: 0,
              },
              to: {
                opacity: 1,
              },
            },
          }}
          series={[
            automationRatesSerie,
            currentStateSerie,
            ...selectedStateSerie,
          ]}
          xAxis={[
            {
              id: X_AXIS_ID,
              zoom: true,
              scaleType: 'linear',
              data: precisionTargets,
              label: intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.charts.optimizationChart.precisionTarget.title',
              }),
              valueFormatter: errorRate =>
                errorRate
                  ? convertRateToPercentage(errorRate)
                  : EMPTY_PLACEHOLDER,
              labelStyle: {
                fill: theme.palette.text.secondary,
                fontWeight: theme.typography.subtitle2.fontWeight,
              },
            },
          ]}
          yAxis={[
            {
              id: 'automation-rate',
              position: 'left',
              colorMap: {
                type: 'continuous',
                min: 0,
                max: 1,
                color: ['transparent', alpha(theme.palette.aurora.main, 0.4)],
              },
              valueFormatter: (value: number) =>
                convertRateToPercentage(value, { padding: 0 }),
              tickLabelInterval: () => true,
              tickInterval: [0.25, 0.5, 0.75, 1],
              label: intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.charts.optimizationChart.automationRate.title',
              }),
              tickLabelPlacement: 'tick',
              labelStyle: {
                fill: theme.palette.text.secondary,
                fontWeight: theme.typography.subtitle2.fontWeight,
              },
            },
          ]}
          margin={{ left: 0, right: 0 }}
        >
          <ChartsClipPath id="clipped" />
          <ChartsGrid horizontal />

          <ChartsXAxis
            disableLine
            disableTicks
            tickLabelStyle={{ fill: theme.palette.text.secondary }}
          />
          <ChartsYAxis
            disableLine
            disableTicks
            tickLabelStyle={{ fill: theme.palette.text.secondary }}
          />
          <g clipPath="url('#clipped')">
            <AreaPlot />
            <LinePlot
              slotProps={{ line: { stroke: theme.palette.aurora.main } }}
            />

            {/* Add gradient layer between current and selected states */}
            {!isLoading && !isDirty && currentState ? (
              <CustomGradientLayer
                currentState={currentState}
                selectedState={targetState ?? currentState}
              />
            ) : null}

            {!isLoading && !isDirty && currentState ? (
              <ChartsReferenceLine
                x={currentState.precisionTarget}
                lineStyle={{
                  stroke: targetState
                    ? theme.palette.text.disabled
                    : theme.palette.primary.main,
                  strokeWidth: targetState ? 1.5 : 2,
                  animation: FADE_IN_ANIMATION,
                  opacity: 0,
                }}
              />
            ) : null}
            {!isLoading && !isDirty && currentState ? (
              <ChartsReferenceLine
                x={targetState?.precisionTarget ?? currentState.precisionTarget}
                lineStyle={{
                  stroke: theme.palette.primary.main,
                  strokeWidth: 2,
                  strokeDasharray: STROKE_DASHARRAY,
                  animation: FADE_IN_ANIMATION,
                  opacity: 0,
                }}
              />
            ) : null}
            <MarkPlot
              onMouseEnter={() => {
                setShouldBlockZoom(true);
              }}
              onMouseLeave={() => setShouldBlockZoom(false)}
              onItemClick={(_, params) => {
                if (params.dataIndex === undefined) return;
                const automationRate = automationRates[params.dataIndex];
                const precisionTarget = precisionTargets[params.dataIndex];
                const newTargetState = automationProjections?.projections.find(
                  p =>
                    p.documentAutomationRate === automationRate &&
                    convertErrorRateToPrecisionTarget(p.estimatedErrorRate) ===
                      precisionTarget
                );

                if (newTargetState) {
                  setTargetState({
                    automationRate: newTargetState.documentAutomationRate,
                    precisionTarget: convertErrorRateToPrecisionTarget(
                      newTargetState.estimatedErrorRate
                    ),
                    datapointStatistics: newTargetState.datapointStatistics,
                  });
                }
              }}
              slotProps={{
                mark: {
                  // @ts-expect-error
                  'data-cy': 'automation-target-point',
                },
              }}
            />

            {!isLoading && !isDirty ? (
              <ScatterPlot
                onItemClick={(_, params) => {
                  if (params.seriesId === CURRENT_STATE_SERIES_ID)
                    setTargetState(null);
                }}
                slotProps={{
                  marker: {
                    // @ts-expect-error
                    'data-cy': 'select-state-point',
                  },
                }}
              />
            ) : null}
          </g>
          <ChartsTooltip
            trigger="item"
            component={props => {
              return (
                <Popper {...props} open={!!tooltipData}>
                  {tooltipData ? (
                    <Paper sx={{ px: 2, py: 1 }}>
                      <Stack direction="row" spacing={1} useFlexGap>
                        <Typography variant="body2" color="text.secondary">
                          {intl.formatMessage({
                            id: 'containers.settings.queues.queue.automation.charts.optimizationChart.tooltipLabel.automationRate',
                          })}
                        </Typography>
                        <Typography variant="body2">
                          {tooltipData.automationRate}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} useFlexGap>
                        <Typography variant="body2" color="text.secondary">
                          {intl.formatMessage({
                            id: 'containers.settings.queues.queue.automation.charts.optimizationChart.tooltipLabel.precisionTarget',
                          })}
                        </Typography>
                        <Typography variant="body2">
                          {tooltipData.precisionTarget}
                        </Typography>
                      </Stack>
                    </Paper>
                  ) : null}
                </Popper>
              );
            }}
          />
        </ChartContainerPro>
      </Stack>
    </Stack>
  );
};
